import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { atomWithStorage } from 'jotai/utils'
import { z } from 'zod'

import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { safeStorage } from '@/lib/safe-storage'
import { Exchange, InstrumentType } from '@/types/generated/common/types'
import { OptionPriceSource, RiskSimulationResponse } from '@/types/generated/public_api'

// Schema
export const riskSlidesFilterSchema = z.object({
  coin: z.string(),
  viewMode: z.enum(['table', 'graph']),
  /** Unset = automatic: today / the farthest expiry among the simulated legs
   * (see resolveRiskDateRange). */
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  priceRangeFrom: z.number(),
  priceRangeTo: z.number(),
  /** 'absolute' = exact prices, 'percent' = % offset from current mark (e.g. 20 → 0.20 sent to API) */
  priceRangeKind: z.enum(['absolute', 'percent']),
  priceStep: z.string(),
  spotVolDynamic: z.string(),
  parallelShift: z.number(),
  timeStepSeconds: z.string(),
  optionPriceSource: z.nativeEnum(OptionPriceSource),
  positionFilters: z.object({
    cm: z.boolean(),
    usdtM: z.boolean(),
    usdcM: z.boolean(),
    expiryFilter: z.boolean(),
    excludeExpired: z.boolean(),
  }),
  /** Per-expiry IV bump (%) keyed by ISO datetime string at 08:00 UTC */
  volBumpsByExpiry: z.record(z.string(), z.number()),
})

export type RiskSlidesFilterValues = z.infer<typeof riskSlidesFilterSchema>

// Default % range used to compute ABS bounds from current price on first load / coin change.
// priceRangeFrom is negative (lower offset), priceRangeTo is positive (upper offset).
export const DEFAULT_PRICE_RANGE_PCT = { from: -20, to: 20 } as const

// Default values
export const DEFAULT_RISK_SLIDES_FILTER_VALUES: RiskSlidesFilterValues = {
  coin: 'BTC',
  viewMode: 'table',
  startDate: undefined,
  endDate: undefined,
  priceRangeFrom: DEFAULT_PRICE_RANGE_PCT.from,
  priceRangeTo: DEFAULT_PRICE_RANGE_PCT.to,
  priceRangeKind: 'percent' as const,
  priceStep: '1%',
  spotVolDynamic: 'Sticky Strike',
  parallelShift: 0,
  timeStepSeconds: '86400',
  optionPriceSource: OptionPriceSource.OPTION_PRICE_SOURCE_MARK,
  positionFilters: {
    cm: true,
    usdtM: false,
    usdcM: false,
    expiryFilter: false,
    excludeExpired: false,
  },
  volBumpsByExpiry: {},
}

// Simulated portfolio instrument
export type SimulatedInstrumentType = 'perpetual' | 'future' | 'call' | 'put'

export type SimulatedInstrument = {
  // Unique identity key, NOT for display — `${exchange}:${symbol}` for manual rows,
  // `${accountId}:${symbol}` for position rows. Two accounts (even on different
  // exchanges) can hold identically-symbol'd instruments, which would silently
  // collide/overwrite each other in the portfolio if `id` were the bare symbol.
  // Use `label` for anything user-facing.
  id: string
  label: string // display symbol, e.g. BTC-PERPETUAL, BTC-27DEC25-50000-C
  asset: string
  exchange: Exchange
  // Present only for source: 'position' rows — the account this position lives on.
  accountId?: string
  size: number
  entryPrice: number
  contractType: InstrumentType
  type: SimulatedInstrumentType
  strike?: number
  expiryDate?: string // YYYY-MM-DD
  // 'position' rows mirror a live account position and are safe to auto-remove once
  // that position is gone (closed, or no longer exists on any connected account).
  // 'manual' rows were added by hand via the instrument search and must never be
  // auto-removed.
  source: 'position' | 'manual'
  // Manual rows the user unchecked on the Risk Slides page: kept in the list (so they
  // can be re-checked) but left out of the simulation request.
  excluded?: boolean
}

// Per-widget-instance state, keyed by cardId — each Risk Slides widget must have its
// own filter/portfolio/response/trigger so switching pages doesn't bleed state across
// other widget instances (a single global atom previously caused stale positions to
// reappear when navigating between pages with separate Risk Slides widgets).
export const riskSlidesFilterAtomFamily = atomFamily((_cardId: string) =>
  atom<RiskSlidesFilterValues>(DEFAULT_RISK_SLIDES_FILTER_VALUES)
)
export const riskSimulationResponseAtomFamily = atomFamily((_cardId: string) =>
  atom<RiskSimulationResponse | null>(null)
)
export const simulatedPortfolioAtomFamily = atomFamily((_cardId: string) =>
  atom<SimulatedInstrument[]>([])
)
// Increment to manually trigger a simulation run
export const riskSimulationTriggerAtomFamily = atomFamily((_cardId: string) => atom(0))

// ── Saved scenarios ──────────────────────────────────────────────────────────

/** A single-asset snapshot of the simulated portfolio, saved into one of a fixed
 * number of slots so the user can switch between a few scenarios without losing
 * work. Restoring one re-syncs against live positions on the next tick (see
 * RiskSlidesPortfolio) and drops any option leg that's since expired. */
export type SavedRiskScenario = {
  id: string
  coin: string
  portfolio: SimulatedInstrument[]
  savedAt: string // ISO timestamp
}

export const RISK_SCENARIO_SLOT_COUNT = 5

// Per-widget-instance, persisted so saved scenarios survive a reload.
export const savedRiskScenariosAtomFamily = atomFamily((cardId: string) =>
  atomWithStorage<(SavedRiskScenario | null)[]>(
    `${STORAGE_KEYS.riskSlides.savedScenarios}-${cardId}`,
    Array(RISK_SCENARIO_SLOT_COUNT).fill(null),
    safeStorage,
    { getOnInit: true }
  )
)
